import { useMemo } from "react";
import * as THREE from "three";

interface LightingProps {
  width: number;
  height: number;
  depth: number;
}

/**
 * Studio-style three-point lighting, sized to the block.
 *
 * A soft key from the front-left casts the contact shadow onto the bench, a
 * cool fill from the opposite side stops carved cavities going black, and a
 * low rim separates the silhouette from the background. Everything is
 * generated locally — no environment map is fetched from anywhere.
 */
export function Lighting({ width, height, depth }: LightingProps): React.ReactElement {
  const span = Math.max(width, depth);
  const target = useMemo(() => {
    const object = new THREE.Object3D();
    object.position.set(width / 2, height * 0.35, depth / 2);
    return object;
  }, [width, height, depth]);

  return (
    <>
      <primitive object={target} />
      <hemisphereLight args={["#dfe6df", "#2b302c", 1.05]} />
      <ambientLight intensity={0.22} color="#e7e9e2" />

      <directionalLight
        castShadow
        position={[width / 2 - span * 0.85, height + span * 1.15, depth / 2 + span * 0.95]}
        intensity={2.1}
        color="#fff6e8"
        target={target}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-bias={-0.0012}
        shadow-normalBias={Math.max(0.4, span * 0.002)}
        shadow-camera-near={1}
        shadow-camera-far={span * 5}
        shadow-camera-left={-span * 1.3}
        shadow-camera-right={span * 1.3}
        shadow-camera-top={span * 1.3}
        shadow-camera-bottom={-span * 1.3}
      />

      <directionalLight
        position={[width / 2 + span * 1.1, height + span * 0.5, depth / 2 - span * 0.5]}
        intensity={0.75}
        color="#cfe0ea"
        target={target}
      />

      <directionalLight
        position={[width / 2, height * 0.4, depth / 2 - span * 1.5]}
        intensity={0.55}
        color="#c8d7bc"
        target={target}
      />
    </>
  );
}
